import React from 'react';
import {
  getHeadcountChartSeriesConfig,
  type THeadcountCategoryLevel,
} from './headcountCategoryConfig';

const formatTableCellValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return 'NA';
  }
  return String(value);
};

const DATE_COLUMN_WIDTH = 100;
// Studios Date column by category level.
const STUDIO_DATE_COLUMN_WIDTH: Record<THeadcountCategoryLevel, number> = {
  level_1: 80,
  level_2: DATE_COLUMN_WIDTH,
  functions: DATE_COLUMN_WIDTH,
};
// Portfolio Studio column widens with denser category levels.
const PORTFOLIO_STUDIO_COLUMN_WIDTH: Record<THeadcountCategoryLevel, number> = {
  level_1: 100,
  level_2: 160,
  functions: 200,
};

// Date spans both header rows when FTE/Temporary groups show — tweak this offset
// to align "Date" with the group titles above the category columns.
const DATE_GROUPED_HEADER_TOP_OFFSET_PX = 25;

// Shared defaults for level 1 and level 2 (level 2 widths are already tuned).
const HEADCOUNT_COLUMN_WIDTHS: Record<string, number> = {
  fte: 200,
  temporary: 200,
  management: 120,
  r_and_d: 80,
  s_and_m: 80,
  liveops: 80,
  functional_support: 120,
  intern: 80,
  contractor: 110,
  others: 80,
};

// Functions has more columns — wider min widths so labels wrap at word boundaries.
const FUNCTIONS_COLUMN_WIDTHS: Record<string, number> = {
  management: 120,
  technology_and_research: 130,
  product_management_and_business_operations_and_development: 280,
  design: 90,
  sales_and_marketing: 110,
  functional_support: 110,
  customer_service: 110,
  intern: 90,
  contractor: 110,
  others: 90,
};

const getColumnWidth = (
  chartKey: string,
  level: THeadcountCategoryLevel,
  scope: 'studio' | 'portfolio' = 'studio',
): number => {
  if (chartKey === 'date') {
    return scope === 'portfolio'
      ? PORTFOLIO_STUDIO_COLUMN_WIDTH[level]
      : STUDIO_DATE_COLUMN_WIDTH[level];
  }
  if (level === 'functions') {
    return FUNCTIONS_COLUMN_WIDTHS[chartKey] || 100;
  }
  return HEADCOUNT_COLUMN_WIDTHS[chartKey] || 100;
};

const renderColumnHeader = (
  label: string,
  textAlign: 'left' | 'right' | 'center' = 'right',
  extraStyle?: React.CSSProperties,
): React.ReactNode => (
  <div
    style={{
      textAlign,
      lineHeight: '15px',
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'break-word',
      ...extraStyle,
    }}
  >
    {label}
  </div>
);

export const getHeadcountTableColumns = (
  level: THeadcountCategoryLevel,
  formatMessage: (descriptor: { id: string }) => string,
  scope: 'studio' | 'portfolio' = 'studio',
  hasLiveops?: boolean,
) => {
  // Keep table columns generated from the same series config as the chart so
  // category order, labels, and keys stay aligned across both views.
  const seriesConfig = getHeadcountChartSeriesConfig(
    level,
    undefined,
    undefined,
    hasLiveops,
  );
  const axisHeader = scope === 'portfolio'
    ? 'Studio'
    : formatMessage({ id: 'dbs_date' });
  // Portfolio keeps API `date` for tooltips; table first column reads `label`.
  const axisDataIndex = scope === 'portfolio' ? 'label' : 'date';

  return [
    {
      fixed: 'left', // Make the first column (Date/Studio) sticky
      header: renderColumnHeader(
        axisHeader,
        'left',
        level !== 'level_1'
          ? { position: 'relative', top: `-${DATE_GROUPED_HEADER_TOP_OFFSET_PX}px` }
          : undefined,
      ),
      title: axisHeader,
      dataIndex: axisDataIndex,
      key: axisDataIndex,
      width: getColumnWidth('date', level, scope),
      render: (item: Record<string, unknown>) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {formatTableCellValue(item?.[axisDataIndex])}
        </div>
      ),
    },
    ...seriesConfig.map((series) => ({
      header: renderColumnHeader(formatMessage({ id: series.labelKey })),
      title: formatMessage({ id: series.labelKey }),
      dataIndex: series.chartKey,
      key: series.chartKey,
      width: getColumnWidth(series.chartKey, level, scope),
      render: (item: Record<string, unknown>) => (
        <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>
          {formatTableCellValue(item?.[series.chartKey])}
        </div>
      ),
    })),
  ];
};

export const getHeadcountTableHeaderGroups = (
  level: THeadcountCategoryLevel,
  formatMessage: (descriptor: { id: string }) => string,
  hasLiveops?: boolean,
) => {
  if (level === 'level_1') {
    return null;
  }

  const seriesConfig = getHeadcountChartSeriesConfig(
    level,
    undefined,
    undefined,
    hasLiveops,
  );
  // Tea table header groups reference child column keys, so derive them from
  // the series config instead of maintaining a second static key list.
  const fteKeys = seriesConfig
    .filter((series) => series.employmentType === 'fte')
    .map((series) => series.chartKey);
  const temporaryKeys = seriesConfig
    .filter((series) => series.employmentType === 'temporary')
    .map((series) => series.chartKey);

  return [
    {
      key: 'fte',
      title: renderColumnHeader(
        formatMessage({ id: 'dbs_studios_headcount_fte' }),
        'center',
      ),
      align: 'center' as const,
      children: fteKeys,
    },
    {
      key: 'temporary',
      title: renderColumnHeader(
        formatMessage({ id: 'dbs_studios_headcount_temporary' }),
        'center',
      ),
      align: 'center' as const,
      children: temporaryKeys,
    },
  ];
};

export const getHeadcountTableMinWidth = (
  level: THeadcountCategoryLevel,
  scope: 'studio' | 'portfolio' = 'studio',
  hasLiveops?: boolean,
): number => {
  const seriesConfig = getHeadcountChartSeriesConfig(
    level,
    undefined,
    undefined,
    hasLiveops,
  );
  const totalWidth = seriesConfig.reduce(
    (sum, series) => sum + getColumnWidth(series.chartKey, level, scope),
    getColumnWidth('date', level, scope),
  );

  // Functions needs a larger scroll area due to the extra sub-columns.
  const widthBuffer = level === 'functions' ? 140 : 78;

  return totalWidth + widthBuffer;
};
